import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending2FA, setPending2FA] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    
    if (response.data.requires_2fa) {
      // Store the pending token (issued after successful password verification)
      setPending2FA({ pendingToken: response.data.pending_token });
      return { requires2FA: true };
    }
    
    setUser(response.data);
    return { success: true };
  };

  const verify2FA = async (code, rememberDevice = false) => {
    if (!pending2FA) {
      throw new Error("No pending 2FA verification");
    }

    const response = await axios.post(`${BACKEND_URL}/api/auth/verify-2fa`,
      { code, remember_device: rememberDevice },
      { 
        withCredentials: true,
        headers: { "X-Pending-Token": pending2FA.pendingToken }
      }
    );
    
    setUser(response.data);
    setPending2FA(null);
    return { success: true };
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    setUser(response.data);
    return { success: true };
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    const isInIframe = window.self !== window.top;

    if (isInIframe) {
      // Inside an iframe (e.g. Emergent preview): use popup to avoid navigating the parent frame
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(authUrl, 'google-auth', `width=${width},height=${height},left=${left},top=${top}`);

      const handleMessage = async (event) => {
        if (event.data?.type === 'google-auth-callback' && event.data?.sessionId) {
          window.removeEventListener('message', handleMessage);
          try {
            await handleGoogleCallback(event.data.sessionId);
          } catch (error) {
            console.error("Google auth error:", error);
          }
        }
      };
      window.addEventListener('message', handleMessage);

      if (!popup || popup.closed) {
        window.removeEventListener('message', handleMessage);
        window.location.href = authUrl;
      }
    } else {
      // Normal browser window (production): standard redirect
      window.location.href = authUrl;
    }
  };

  const handleGoogleCallback = async (sessionId) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/google/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  const setup2FA = async () => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/setup`, {}, {
      withCredentials: true
    });
    return response.data;
  };

  const enable2FA = async (code) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/enable`,
      { code },
      { withCredentials: true }
    );
    setUser(prev => ({ ...prev, totp_enabled: true }));
    return response.data;
  };

  const disable2FA = async (code) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/disable`,
      { code },
      { withCredentials: true }
    );
    setUser(prev => ({ ...prev, totp_enabled: false }));
    return response.data;
  };

  const value = {
    user,
    loading,
    pending2FA,
    login,
    verify2FA,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    setup2FA,
    enable2FA,
    disable2FA,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
